import React, { useEffect, useRef, useState } from 'react';

// مكون لمراقبة الأداء - نسخة محسنة لتجنب إعادة الرندر اللانهائية
const PerformanceMonitor = ({
  children,
  trackRenders = false, // معطل افتراضياً لتجنب المشاكل
  trackMemory = false,  // معطل افتراضياً لتجنب المشاكل
  logToConsole = process.env.NODE_ENV === 'development'
}) => {
  const renderCount = useRef(0);
  const lastLogTime = useRef(performance.now());
  const mountTime = useRef(performance.now());

  // Track renders only when enabled and log sparingly
  if (trackRenders && process.env.NODE_ENV === 'development') {
    renderCount.current += 1;
    const currentTime = performance.now();

    // Only log every 50 renders or if it's been more than 5 seconds
    if (renderCount.current % 50 === 0 || (currentTime - lastLogTime.current) > 5000) {
      if (logToConsole) {
        console.log(`PerformanceMonitor: ${renderCount.current} renders in ${((currentTime - mountTime.current) / 1000).toFixed(2)}s`);
      }
      lastLogTime.current = currentTime;
    }
  }

  useEffect(() => {
    if (trackMemory && 'memory' in performance && process.env.NODE_ENV === 'development') {
      const updateMemoryUsage = () => {
        const memoryInfo = performance.memory;
        const memoryUsageInMB = memoryInfo.usedJSHeapSize / 1024 / 1024;

        // Only log if memory usage is high to avoid spam
        if (logToConsole && memoryUsageInMB > 100) {
          console.warn(`High memory usage: ${memoryUsageInMB.toFixed(2)}MB`);
        }
      };

      // Check memory less frequently to avoid performance impact
      const interval = setInterval(updateMemoryUsage, 30000); // كل 30 ثانية
      updateMemoryUsage(); // قياس فوري

      return () => clearInterval(interval);
    }
  }, [trackMemory, logToConsole]);

  return children;
};

// Hook لمراقبة أداء المكونات
export const usePerformanceMonitor = (componentName) => {
  const renderCount = useRef(0);
  const mountTime = useRef(performance.now());
  const lastRenderTime = useRef(performance.now());

  // Track renders without causing re-renders
  renderCount.current += 1;
  const currentTime = performance.now();
  const renderTime = currentTime - lastRenderTime.current;

  if (process.env.NODE_ENV === 'development') {
    // Only log occasionally to avoid spam
    if (renderCount.current === 1 || renderCount.current % 10 === 0) {
      console.log(`${componentName} - Render #${renderCount.current}, Time: ${renderTime.toFixed(2)}ms`);
    }

    if (renderTime > 16) {
      console.warn(`${componentName} - Slow render: ${renderTime.toFixed(2)}ms`);
    }
  }

  lastRenderTime.current = currentTime;

  useEffect(() => {
    return () => {
      if (process.env.NODE_ENV === 'development') {
        const totalTime = performance.now() - mountTime.current;
        console.log(`${componentName} - Unmounted after ${totalTime.toFixed(2)}ms, Total renders: ${renderCount.current}`);
      }
    };
  }, [componentName]);

  return {
    renderCount: renderCount.current,
    totalTime: performance.now() - mountTime.current
  };
};

// Hook لمراقبة استخدام الذاكرة
export const useMemoryMonitor = (interval = 5000) => {
  const [memoryUsage, setMemoryUsage] = useState(0);

  useEffect(() => {
    if (!('memory' in performance)) {
      return;
    }

    const updateMemoryUsage = () => {
      const memoryInfo = performance.memory;
      const usageInMB = memoryInfo.usedJSHeapSize / 1024 / 1024;
      setMemoryUsage(usageInMB);

      if (process.env.NODE_ENV === 'development' && usageInMB > 100) {
        console.warn(`High memory usage: ${usageInMB.toFixed(2)}MB`);
      }
    };

    updateMemoryUsage();
    const intervalId = setInterval(updateMemoryUsage, interval);

    return () => clearInterval(intervalId);
  }, [interval]);

  return memoryUsage;
};

// Hook لمراقبة أداء API
export const useAPIPerformanceMonitor = () => {
  const [apiMetrics, setApiMetrics] = useState({
    totalRequests: 0,
    averageResponseTime: 0,
    slowRequests: 0,
    failedRequests: 0
  });

  const trackRequest = (url, startTime, endTime, success = true) => {
    const responseTime = endTime - startTime;

    setApiMetrics(prev => {
      const newTotal = prev.totalRequests + 1;
      const newAverage = (prev.averageResponseTime * prev.totalRequests + responseTime) / newTotal;
      const slowRequests = responseTime > 1000 ? prev.slowRequests + 1 : prev.slowRequests; // أبطأ من ثانية
      const failedRequests = !success ? prev.failedRequests + 1 : prev.failedRequests;

      return {
        totalRequests: newTotal,
        averageResponseTime: newAverage,
        slowRequests,
        failedRequests
      };
    });

    if (process.env.NODE_ENV === 'development') {
      if (responseTime > 1000) {
        console.warn(`Slow API request to ${url}: ${responseTime.toFixed(2)}ms`);
      }

      if (!success) {
        console.error(`Failed API request to ${url}`);
      }
    }
  };

  return { apiMetrics, trackRequest };
};

// مكون لعرض معلومات الأداء في وضع التطوير
export const PerformanceDebugger = () => {
  const [isVisible, setIsVisible] = useState(false);
  const memoryUsage = useMemoryMonitor();
  const [renderMetrics, setRenderMetrics] = useState({
    totalRenders: 0,
    slowRenders: 0
  });

  useEffect(() => {
    // إظهار المعلومات فقط في وضع التطوير
    if (process.env.NODE_ENV === 'development') {
      const handleKeyDown = (e) => {
        if (e.ctrlKey && e.shiftKey && e.key === 'P') {
          setIsVisible(prev => !prev);
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, []);

  if (process.env.NODE_ENV !== 'development' || !isVisible) {
    return null;
  }

  return (
    <div style={{
      position: 'fixed',
      top: '10px',
      right: '10px',
      background: 'rgba(0, 0, 0, 0.8)',
      color: 'white',
      padding: '10px',
      borderRadius: '5px',
      fontSize: '12px',
      zIndex: 9999,
      fontFamily: 'monospace'
    }}>
      <div>Memory: {memoryUsage.toFixed(2)}MB</div>
      <div>Renders: {renderMetrics.totalRenders}</div>
      <div>Slow Renders: {renderMetrics.slowRenders}</div>
      <div style={{ marginTop: '5px', fontSize: '10px', opacity: 0.7 }}>
        Press Ctrl+Shift+P to toggle
      </div>
    </div>
  );
};

// HOC لمراقبة أداء المكونات
export const withPerformanceMonitoring = (WrappedComponent, componentName) => {
  const MonitoredComponent = React.memo((props) => {
    usePerformanceMonitor(componentName || WrappedComponent.displayName || WrappedComponent.name);

    return (
      <PerformanceMonitor>
        <WrappedComponent {...props} />
      </PerformanceMonitor>
    );
  });

  MonitoredComponent.displayName = `withPerformanceMonitoring(${componentName || WrappedComponent.displayName || WrappedComponent.name})`;

  return MonitoredComponent;
};

// دالة لقياس أداء العمليات
export const measurePerformance = (name, fn) => {
  return async (...args) => {
    const startTime = performance.now();

    try {
      const result = await fn(...args);
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (process.env.NODE_ENV === 'development') {
        console.log(`${name} completed in ${duration.toFixed(2)}ms`);

        if (duration > 100) {
          console.warn(`${name} took longer than expected: ${duration.toFixed(2)}ms`);
        }
      }

      return result;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (process.env.NODE_ENV === 'development') {
        console.error(`${name} failed after ${duration.toFixed(2)}ms:`, error);
      }

      throw error;
    }
  };
};

// Hook لتتبع تحديثات الحالة غير الضرورية
export const useRenderTracker = (props, componentName) => {
  const prevProps = useRef();
  const renderCount = useRef(0);

  useEffect(() => {
    renderCount.current += 1;

    if (prevProps.current && process.env.NODE_ENV === 'development') {
      const changedProps = Object.keys(props).filter(
        key => prevProps.current[key] !== props[key]
      );

      if (changedProps.length === 0) {
        console.warn(`${componentName} re-rendered without prop changes. Render #${renderCount.current}`);
      } else {
        console.log(`${componentName} re-rendered due to props:`, changedProps);
      }
    }

    prevProps.current = props;
  });

  return renderCount.current;
};

export default PerformanceMonitor;
